import { Track } from "@/src/lib/tracking";
import { useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";

import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { type OwnerPreference } from "@features/api/memberApi";
import { rootStore } from "@store/rootStore";
import { QUERY_KEYS } from "@config/constant";

interface UseOwnerPreferenceOptions {
  /** 是否启用查询，默认 true */
  enabled?: boolean;
}

/**
 * 获取当前登录用户的偏好设置（agent_ids、risk_level、investment_goals 等）
 * 登录且 memberApi 可用时自动请求，5 分钟缓存。
 */
export const useOwnerPreference = (options?: UseOwnerPreferenceOptions) => {
  const { enabled = true } = options || {};
  const { memberApi } = useContext(OspApiClientContext);
  const { isLoggedIn, isFetching } = useSnapshot(rootStore.login);
  const [ownerPreference, setOwnerPreference] =
    useState<OwnerPreference | null>(null);
  const [preferenceLoading, setPreferenceLoading] = useState(true);
  const { data: ownerPreferenceFromApi, isLoading } =
    useQuery<OwnerPreference | null>({
      queryKey: [QUERY_KEYS.AGENT_PREFERENCES, !!memberApi, isLoggedIn],
      enabled: enabled && !!memberApi && isLoggedIn && !isFetching,
      staleTime: 5 * 60 * 1000,
      retry: 2,
      queryFn: async () => {
        try {
          const response = await memberApi?.agents?.getPreference();
          const preference = response?.data?.data || null;
          return preference;
        } catch (error) {
          Track.error(error, {
            tag: "useOwnerPreference",
            msg: "Fetch preference error",
          });
          return null;
        }
      },
    });

  useEffect(() => {
    /**
     * 如果已登录，则从 API 中获取偏好设置
     */
    if (isLoggedIn && !isFetching) {
      setOwnerPreference(ownerPreferenceFromApi || null);
      setPreferenceLoading(false);
    }
  }, [isLoggedIn, isFetching, ownerPreferenceFromApi]);

  return {
    ownerPreference,
    isLoading: isLoading || preferenceLoading,
  };
};
